import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { DiscordSDK, patchUrlMappings } from "@discord/embedded-app-sdk";

const AuthContext = createContext(null);

const DISCORD_CLIENT_ID = process.env.REACT_APP_DISCORD_CLIENT_ID;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_HOST = BACKEND_URL?.replace('https://', '').replace('http://', '');

// Debug logs array
const debugLogs = [];
function addLog(msg) {
  const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
  const entry = `[${timestamp}] ${msg}`;
  debugLogs.push(entry);
  console.log('[Debug]', msg);
}

// Environment detection
function detectEnvironment() {
  const info = {
    isIframe: false,
    hasFrameId: false,
    hasInstanceId: false,
    hasDiscordNative: false,
    userAgent: navigator.userAgent.substring(0, 50),
    href: window.location.href.substring(0, 100),
  };
  
  try {
    info.isIframe = window.self !== window.top;
  } catch {
    info.isIframe = true; // Cross-origin means iframe
  }
  
  const params = new URLSearchParams(window.location.search);
  info.hasFrameId = params.has('frame_id');
  info.hasInstanceId = params.has('instance_id');
  info.hasDiscordNative = typeof window.DiscordNative !== 'undefined';
  
  return info;
}

// Check if we're in Discord environment
function isDiscordEnvironment() {
  const params = new URLSearchParams(window.location.search);
  // Discord Activity always has frame_id and instance_id
  return params.has('frame_id') && params.has('instance_id');
}

// Apply URL mappings - only in Discord environment
let urlMappingsPatched = false;
function applyUrlMappings() {
  if (urlMappingsPatched) {
    addLog(`patchUrlMappings skipped: already patched`);
    return;
  }
  // Only apply in Discord environment
  if (!isDiscordEnvironment()) {
    addLog(`patchUrlMappings skipped: not in Discord environment`);
    return;
  }
  try {
    // Use BACKEND_HOST from environment variable
    patchUrlMappings([{ prefix: '/', target: BACKEND_HOST }]);
    urlMappingsPatched = true;
    addLog(`patchUrlMappings OK: / -> ${BACKEND_HOST}`);
  } catch (e) {
    addLog(`patchUrlMappings FAIL: ${e.message}`);
  }
}

// Singleton
let sdk = null;
let isReady = false;
let initPromise = null;
let cachedUser = null;

async function initializeDiscord() {
  // Only initialize in Discord environment
  if (!isDiscordEnvironment()) {
    addLog('Not in Discord environment, skipping Discord auth');
    return { success: false, error: 'Not in Discord environment' };
  }

  if (isReady && cachedUser) {
    addLog('Already initialized, returning cached');
    return { success: true, user: cachedUser };
  }

  if (initPromise) {
    addLog('Init in progress, waiting...');
    return initPromise;
  }

  initPromise = (async () => {
    try {
      addLog('Step 1: new DiscordSDK()');
      sdk = new DiscordSDK(DISCORD_CLIENT_ID);

      addLog('Step 2: sdk.ready()');
      await Promise.race([
        sdk.ready(),
        new Promise((_, rej) => setTimeout(() => rej(new Error('ready timeout 5s')), 5000))
      ]);
      addLog('Step 2: OK');

      addLog('Step 3: authorize()');
      const { code } = await sdk.commands.authorize({
        client_id: DISCORD_CLIENT_ID,
        response_type: "code",
        state: "",
        prompt: "none",
        scope: ["identify", "guilds"],
      });
      addLog(`Step 3: OK code=${code.substring(0,10)}...`);

      addLog('Step 4: fetch /api/discord/token');
      const resp = await fetch("/api/discord/token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      addLog(`Step 4: status=${resp.status}`);

      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`Token ${resp.status}: ${txt.substring(0,100)}`);
      }

      const { access_token } = await resp.json();
      addLog('Step 4: OK got token');

      addLog('Step 5: authenticate()');
      const auth = await sdk.commands.authenticate({ access_token });
      
      if (!auth?.user) throw new Error("No user in auth");
      addLog(`Step 5: OK user=${auth.user.username}`);

      cachedUser = {
        id: "discord_" + auth.user.id,
        nickname: auth.user.global_name || auth.user.username,
        avatar: auth.user.avatar 
          ? `https://cdn.discordapp.com/avatars/${auth.user.id}/${auth.user.avatar}.png`
          : null,
        isDiscord: true,
        discordId: auth.user.id
      };
      isReady = true;
      addLog('DONE: Discord auth success');

      return { success: true, user: cachedUser };

    } catch (e) {
      addLog(`ERROR: ${e.message}`);
      return { success: false, error: e.message };
    }
  })();

  try {
    return await initPromise;
  } finally {
    initPromise = null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDiscord, setIsDiscord] = useState(false);
  const [error, setError] = useState(null);
  const [envInfo, setEnvInfo] = useState(null);
  const [logs, setLogs] = useState([]);

  const getBrowserUserId = useCallback(() => {
    let id = localStorage.getItem("akindo_browser_id");
    if (!id) {
      id = "browser_" + crypto.randomUUID();
      localStorage.setItem("akindo_browser_id", id);
    }
    return id;
  }, []);

  const initBrowserUser = useCallback(() => {
    return {
      id: getBrowserUserId(),
      nickname: localStorage.getItem("akindo_nickname") || null,
      avatar: null,
      isDiscord: false
    };
  }, [getBrowserUserId]);

  const setNickname = useCallback((nickname) => {
    if (!user?.isDiscord) {
      localStorage.setItem("akindo_nickname", nickname);
      setUser(prev => ({ ...prev, nickname }));
    }
  }, [user]);

  useEffect(() => {
    let mounted = true;

    async function init() {
      // Detect environment
      const env = detectEnvironment();
      addLog(`ENV: iframe=${env.isIframe}, frame_id=${env.hasFrameId}, instance_id=${env.hasInstanceId}, DiscordNative=${env.hasDiscordNative}`);
      setEnvInfo(env);

      // Check if we're in Discord environment
      if (isDiscordEnvironment()) {
        addLog('Detected Discord environment, starting Discord auth...');
        // Apply URL mappings for Discord
        applyUrlMappings();
        
        // Try Discord auth
        const result = await initializeDiscord();
        
        if (mounted) {
          setLogs([...debugLogs]);
          if (result.success) {
            setUser(result.user);
            setIsDiscord(true);
          } else {
            setError(result.error);
            // Fall back to browser mode even in Discord if auth fails
            setUser(initBrowserUser());
            setIsDiscord(false);
          }
          setIsLoading(false);
        }
      } else {
        // Browser mode - skip Discord auth entirely
        addLog('Browser mode detected, using local auth');
        if (mounted) {
          setLogs([...debugLogs]);
          setUser(initBrowserUser());
          setIsDiscord(false);
          setIsLoading(false);
        }
      }
    }

    init();
    return () => { mounted = false; };
  }, [initBrowserUser]);

  // Update logs periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setLogs([...debugLogs]);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const value = {
    user,
    isLoading,
    isDiscord,
    error,
    setNickname,
    apiUrl: '/api',
    envInfo,
    logs,
    getAvatarUrl: () => {
      if (user?.avatar) return user.avatar;
      const colors = ["#5865F2", "#57F287", "#FEE75C", "#EB459E", "#ED4245"];
      const idx = user?.id ? user.id.charCodeAt(0) % colors.length : 0;
      return `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64"><rect width="64" height="64" fill="${encodeURIComponent(colors[idx])}"/><text x="32" y="42" font-size="32" text-anchor="middle" fill="white">${encodeURIComponent(user?.nickname?.[0]?.toUpperCase() || "?")}</text></svg>`;
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export const getApiBaseUrl = () => '/api';
